import { Vault } from "../../libs/storage.js";
import type { Tracking, UTMValues } from "./Tracking";
import { reportStorageFallback } from "./CoreReport";

export { ClickIds, ClickIdEntry, ClickCaptureDefinition, ClickCaptureDefsDocument };

/**
 * Ad-network click ID capture, DEFINITION-DRIVEN.
 *
 * Each ad network appends its own click identifier to the landing URL
 * (gclid, fbclid, ttclid...). We capture them and keep the FULL history in
 * localStorage (`t_click_ids`), associated to the visitor via the shared
 * `t_visitor_id`, so conversions can later be reported to each network's
 * Conversion API.
 *
 * The SOURCE OF TRUTH for "how each network captures" lives in the backend
 * (protodeal-api-marketing NetworkClickCaptureRegistry, keyed by
 * ad_network.slug, exposed via GET /click-capture-defs) and reaches us
 * INSIDE the config-values payload (aggregated by api-core, cached by the
 * config-values lambda with Redis TTL + stale-on-error), which the loader
 * awaits BEFORE EventCollector.init — so the definitions are available at
 * construction time, no extra fetch needed.
 *
 * BUILT_IN_DEFINITIONS is a resilience FALLBACK only (config loaded without
 * defs — e.g. marketing down at aggregation time): a snapshot of the
 * currently active catalog networks. The backend definitions always override
 * it, and adding a NEW network requires no front deploy (it arrives with the
 * config).
 *
 * NOTE: click ID values are case-sensitive. Unlike UTMs (Tracking lowercases
 * the whole query string) we parse the raw query string and only match the
 * parameter NAMES case-insensitively, preserving the original value.
 *
 * NOTE: we deliberately do NOT remove click ID params from the URL: network
 * pixels (e.g. Meta) read them on load to set their own first-party cookies.
 */

interface ClickCaptureDefinition {
  // ad_network.slug from the marketing catalog (google_ads, meta_ads, ...)
  slug: string;
  // Query params carrying the click ID (lowercase canonical names)
  params: string[];
  // Cookie name -> metadata key its Conversion API needs (e.g. _fbp -> fbp)
  cookies: Record<string, string>;
  // URL fragment prefix carrying the click ID (e.g. Taboola "#tblci...")
  fragment_prefix?: string | null;
  // Meta: derive fbc from the fbclid when the _fbc cookie is missing
  derive_fbc_from_click?: boolean;
  // Params de OTRAS redes que pertenecen a ESTA cuando su propio click param
  // (o fragmento) viene en la misma URL: esos candidatos no se capturan para
  // la otra red. Whop Ads reclama el `fbclid` (ver _dropClaimedCandidates).
  // Opcional: las definiciones sin él no reclaman nada.
  claimed_params?: string[];
}

interface ClickCaptureDefsDocument {
  version: number;
  networks: ClickCaptureDefinition[];
}

interface ClickIdEntry {
  network: string; // ad_network.slug
  type: string; // captured param name (gclid, fbclid, ...)
  value: string;
  utms: Partial<UTMValues>;
  landing_url: string;
  referrer: string;
  captured_at: number;
  // Per-network Conversion API artifacts, attached lazily at message-build
  // time (pixels set their cookies async): meta -> {fbp, fbc}, tiktok -> {ttp}
  metadata?: Record<string, string>;
}

// Resilience fallback: snapshot of the ACTIVE catalog networks (keep in sync
// with the ad_network catalog + NetworkClickCaptureRegistry). The backend
// definitions delivered with the config override this list.
const BUILT_IN_DEFINITIONS: ClickCaptureDefinition[] = [
  { slug: "google_ads", params: ["gclid", "gbraid", "wbraid"], cookies: {} },
  // Microsoft Advertising appends msclkid with auto-tagging on
  { slug: "bing_ads", params: ["msclkid"], cookies: {} },
  {
    slug: "meta_ads",
    params: ["fbclid"],
    cookies: { _fbp: "fbp", _fbc: "fbc" },
    derive_fbc_from_click: true,
  },
  { slug: "taboola", params: ["tblci"], cookies: {}, fragment_prefix: "tblci" },
  { slug: "outbrain", params: ["ob_click_id"], cookies: {} },
  // ChatGPT Ads: OpenAI appends `oppref` to the landing URL; `__obref` is the
  // browser reference its own pixel sets (only present if that pixel is installed)
  { slug: "chatgpt_ads", params: ["oppref"], cookies: { __obref: "obref" } },
  // LinkedIn only appends li_fat_id when the account's Insight Tag has enhanced
  // conversion tracking enabled
  { slug: "linkedin_ads", params: ["li_fat_id"], cookies: {} },
  // Snapchat appends ScCid (matched case-insensitively); _scid is its pixel's cookie
  { slug: "snapchat_ads", params: ["sccid"], cookies: { _scid: "sc_cookie1" } },
  // TikTok appends ttclid; _ttp is the browser id its pixel sets (`ttp` in its
  // Events API)
  { slug: "tiktok_ads", params: ["ttclid"], cookies: { _ttp: "ttp" } },
  // MGID appends nothing with Postback tracking: mgid_cid={click_id} is the house
  // convention in the campaign tracking tags; adclid is MGID's own system param
  { slug: "mgid", params: ["mgid_cid", "adclid"], cookies: {} },
  // Whop Ads: `waid` (id del anuncio de Whop) es su click param. `_wuid` es el
  // visitor id que pone el píxel de Whop — la clave con la que su Events API
  // atribuye los eventos de servidor —; `_fbp`/`_fbc` van al contexto de su
  // Events API. Reclama el `fbclid`: Whop compra en Meta desde cuentas de
  // agencia PROPIAS, así que el fbclid de su clic no es un clic de la cuenta
  // de Meta del tenant (capturarlo como meta_ads haría que nuestro CAPI de
  // Meta le reportase también las conversiones de Whop).
  {
    slug: "whop_ads",
    params: ["waid"],
    cookies: { _wuid: "wuid", _fbp: "fbp", _fbc: "fbc" },
    claimed_params: ["fbclid"],
  },
];

const STORAGE_KEY = "t_click_ids";
const STORAGE_EXPIRES = "365d";
const MAX_STORED_ENTRIES = 20;

class ClickIds {
  private _tracking: Tracking;
  private _definitions: Map<string, ClickCaptureDefinition>;
  private _capturedThisPageLoad = false;
  // Copia en memoria de las entradas persistidas (fallback ante purga de
  // storage en mitad de la sesión)
  private _memoryEntries: ClickIdEntry[] = [];

  private _entryUrl: string;

  constructor(
    tracking: Tracking,
    remoteDefs?: ClickCaptureDefsDocument | null,
    entryUrl?: string,
  ) {
    this._tracking = tracking;
    // URL ORIGINAL de aterrizaje, capturada ANTES de que Tracking reescriba
    // la URL (quita los utm_* con replaceState): las CAPIs (p.ej. Meta
    // event_source_url) esperan la URL completa con la que llegó el usuario.
    this._entryUrl = entryUrl || window.location.href;
    this._definitions = new Map(
      BUILT_IN_DEFINITIONS.map((def) => [def.slug, def]),
    );
    this._applyRemoteDefinitions(remoteDefs);
    this._captureFromUrl();
  }

  /**
   * True when this page load captured a click ID not seen before.
   * Used to decide whether to attach the click history to the entry
   * analytics event (we only need to transmit it once per new click).
   */
  get capturedThisPageLoad(): boolean {
    return this._capturedThisPageLoad;
  }

  /**
   * Click history for the LeadCreation payload, JSON-SERIALIZED: the CRM
   * message pipeline (DataValidation::getMessage) casts every value to
   * string, so an array would arrive as the literal "Array". The consumer
   * json_decodes it back (ConsumerBase::getClickIdsFromMessage).
   */
  getSerializedDataForLead(): { click_ids?: string } {
    const data = this.getAdditionalDataToMessage();
    return data.click_ids
      ? { click_ids: JSON.stringify(data.click_ids) }
      : {};
  }

  /**
   * Full click history for outgoing messages, enriched at build time with
   * the per-network cookies its Conversion API needs (pixels set them async,
   * so they may not exist yet at capture time).
   */
  getAdditionalDataToMessage(): { click_ids?: ClickIdEntry[] } {
    const stored = this._getStored();
    if (stored.length === 0) {
      return {};
    }

    const enriched = stored.map((entry) => {
      const metadata = this._buildNetworkMetadata(entry);
      return Object.keys(metadata).length > 0
        ? { ...entry, metadata: { ...metadata, ...entry.metadata } }
        : entry;
    });

    return { click_ids: enriched };
  }

  /**
   * True si el visitante tiene guardado un clic de esa red (`ad_network.slug`)
   * capturado hace menos de `maxAgeMs`: el visitante viene de esa red. Lo usa
   * la navegación del optin para decidir si espera a su Lead de navegador.
   */
  hasRecentClick(network: string, maxAgeMs: number): boolean {
    const oldest = Date.now() - maxAgeMs;
    return this._getStored().some(
      (entry) =>
        entry.network === network &&
        typeof entry.captured_at === "number" &&
        entry.captured_at >= oldest,
    );
  }

  private _applyRemoteDefinitions(
    remoteDefs?: ClickCaptureDefsDocument | null,
  ): void {
    if (!remoteDefs || !Array.isArray(remoteDefs.networks)) {
      return;
    }

    for (const raw of remoteDefs.networks) {
      if (!raw || typeof raw.slug !== "string" || !Array.isArray(raw.params)) {
        continue;
      }
      this._definitions.set(raw.slug, {
        slug: raw.slug,
        // Lowercase defensivo: el matching de nombres de param es contra un
        // mapa lowercaseado — un param remoto en mixed-case nunca matchearía
        params: raw.params
          .filter((p: unknown) => typeof p === "string")
          .map((p: string) => p.toLowerCase()),
        cookies:
          raw.cookies && typeof raw.cookies === "object" && !Array.isArray(raw.cookies)
            ? raw.cookies
            : {},
        fragment_prefix:
          typeof raw.fragment_prefix === "string" ? raw.fragment_prefix : null,
        derive_fbc_from_click: raw.derive_fbc_from_click === true,
        // Mismo trato defensivo que `params`: solo cadenas, en minúsculas.
        // OJO: la definición remota SUSTITUYE a la built-in del mismo slug,
        // así que una whop_ads remota sin el campo no reclama nada.
        claimed_params: Array.isArray(raw.claimed_params)
          ? raw.claimed_params
              .filter((p: unknown) => typeof p === "string" && p !== "")
              .map((p: string) => p.toLowerCase())
          : [],
      });
    }
  }

  private _captureFromUrl(): void {
    const found = this._searchClickIdsInUrl();
    if (found.length === 0) {
      return;
    }

    const utms = this._tracking.getAdditionalDataToMessage();
    const stored = this._getStored();
    const now = Date.now();
    let appended = false;

    for (const { network, type, value } of found) {
      const alreadyStored = stored.some(
        (entry) => entry.type === type && entry.value === value,
      );
      if (alreadyStored) {
        continue;
      }

      stored.push({
        network,
        type,
        value,
        utms,
        landing_url: this._entryUrl,
        referrer: document.referrer,
        captured_at: now,
      });
      appended = true;
      this._capturedThisPageLoad = true;
    }

    if (appended) {
      // Keep the most recent entries when over the cap
      this._save(stored.slice(-MAX_STORED_ENTRIES));
    }
  }

  private _searchClickIdsInUrl(): {
    network: string;
    type: string;
    value: string;
  }[] {
    // Raw (non-lowercased) query string: click ID values are case-sensitive
    const rawParams = new URLSearchParams(window.location.search);
    const paramsByLowerName = new Map<string, string>();
    rawParams.forEach((value, name) => {
      const lower = name.toLowerCase();
      if (!paramsByLowerName.has(lower)) {
        paramsByLowerName.set(lower, value);
      }
    });

    const fragment = window.location.hash;
    const found: { network: string; type: string; value: string }[] = [];

    this._definitions.forEach((definition) => {
      for (const param of definition.params) {
        const value = paramsByLowerName.get(param);
        if (value) {
          found.push({ network: definition.slug, type: param, value });
        }
      }

      const prefix = definition.fragment_prefix;
      if (
        prefix &&
        fragment.startsWith("#" + prefix) &&
        fragment.length > prefix.length + 1 &&
        !found.some((entry) => entry.network === definition.slug)
      ) {
        found.push({
          network: definition.slug,
          type: definition.params[0] || prefix,
          value: fragment.slice(prefix.length + 1),
        });
      }
    });

    return this._dropClaimedCandidates(found);
  }

  /**
   * Reclamaciones entre redes: una red cuyo PROPIO click param (o fragmento)
   * está en la URL reclama los params que declara en `claimed_params`, y los
   * candidatos de OTRAS redes con ese `type` se descartan.
   *
   * Caso real: el clic de Whop Ads trae `waid` + el `fbclid` que añade Meta,
   * pero la cuenta de Meta es de Whop, no del tenant — sin esto se capturaba
   * también como clic de meta_ads y nuestro CAPI de Meta reportaba las
   * conversiones de Whop al píxel del tenant (decisión de producto: Whop se
   * comporta como cualquier otra red). El fbclid no se pierde: sigue en el
   * `landing_url` de la entrada de Whop. Sin clic de Whop en la URL, el
   * fbclid se captura para Meta exactamente como siempre.
   */
  private _dropClaimedCandidates(
    found: { network: string; type: string; value: string }[],
  ): { network: string; type: string; value: string }[] {
    const claimers = new Map<string, string[]>();
    for (const candidate of found) {
      const claimed = this._definitions.get(candidate.network)?.claimed_params;
      for (const param of Array.isArray(claimed) ? claimed : []) {
        const networks = claimers.get(param) ?? [];
        if (networks.indexOf(candidate.network) === -1) {
          networks.push(candidate.network);
        }
        claimers.set(param, networks);
      }
    }
    if (claimers.size === 0) {
      return found;
    }

    return found.filter((candidate) =>
      (claimers.get(candidate.type) ?? []).every(
        (network) => network === candidate.network,
      ),
    );
  }

  private _buildNetworkMetadata(entry: ClickIdEntry): Record<string, string> {
    const definition = this._definitions.get(entry.network);
    if (!definition) {
      return {};
    }

    const metadata: Record<string, string> = {};

    for (const [cookieName, metadataKey] of Object.entries(
      definition.cookies,
    )) {
      const value = this._getCookie(cookieName);
      if (value) {
        metadata[metadataKey] = value;
      }
    }

    if (
      definition.derive_fbc_from_click &&
      entry.type === "fbclid" &&
      !metadata.fbc
    ) {
      metadata.fbc = `fb.1.${entry.captured_at}.${entry.value}`;
    }

    return metadata;
  }

  private _getStored(): ClickIdEntry[] {
    const stored = Vault.get(STORAGE_KEY);
    if (Array.isArray(stored)) {
      return stored;
    }
    // El localStorage puede purgarse EN MITAD de la sesión (WKWebView/ITP,
    // hallazgo 31/7/2026): sin la clave, caer a la copia en memoria que
    // _save deja siempre — antes se perdían los click ids (gclid/fbclid) y
    // el lead entraba sin atribución de campaña.
    if (this._memoryEntries.length > 0) {
      reportStorageFallback("clickids", "memory");
      return this._memoryEntries;
    }
    return [];
  }

  private _save(entries: ClickIdEntry[]): void {
    this._memoryEntries = entries;
    try {
      Vault.set(STORAGE_KEY, entries, { expires: STORAGE_EXPIRES });
    } catch {
      // Quota/Safari privado: perder el persist no debe abortar el init del
      // EventCollector (dejaría la página entera sin analytics)
    }
  }

  private _getCookie(name: string): string {
    // Escapado SIEMPRE: el nombre viene de definiciones remotas — un
    // metacaracter regex sin escapar lanzaría SyntaxError al construir el
    // mensaje y el lead entero no se enviaría
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const match = document.cookie.match(
      new RegExp("(?:^|;\\s*)" + escaped + "=([^;]*)"),
    );
    return match ? decodeURIComponent(match[1]) : "";
  }
}
